import { useEffect, useRef, useState } from "react";
import { Upload, Download, Repeat, FileText, Image as ImageIcon, ChevronDown, ChevronUp, Camera, Eye, X, Trash2 } from "lucide-react";
import ConfirmAction from "@/components/ConfirmAction";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DOC_TYPES, docTypeLabel } from "@/constants/documentTypes";
import { fmtDate, fmtDateTime } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";

export const ACCEPT = "application/pdf,image/*,.doc,.docx,.xls,.xlsx,.csv,.txt";
const DOCX_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

// Uploads each file as its own document. Returns the files that failed
// (with the server's message) so callers can report them individually.
export async function uploadDocuments(entityType, entityId, files, docType) {
  const failed = [];
  for (const file of files) {
    const form = new FormData();
    form.append("file", file);
    form.append("doc_type", docType);
    form.append("entity_type", entityType);
    form.append("entity_id", entityId);
    try {
      await api.post("/documents", form, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (e) {
      const tooLarge = e.response?.status === 413;
      const detail = typeof e.response?.data?.detail === "string" ? e.response.data.detail : null;
      failed.push({ name: file.name, message: tooLarge ? "File is too large — the limit is 25MB per file" : detail || "Upload failed" });
    }
  }
  return failed;
}

export const previewKind = (doc) => {
  const type = doc.content_type || "";
  if (type.startsWith("image/")) return "image";
  if (type === "application/pdf") return "pdf";
  if (type === DOCX_TYPE || /\.docx$/i.test(doc.filename)) return "docx";
  if (type === "text/csv" || type === "text/plain" || /\.(csv|txt)$/i.test(doc.filename || "")) return "text";
  return null;
};

// Uploaded files come from other users (vendors too), so the converted
// HTML keeps only http(s)/mailto links, inline images, and no handlers.
function sanitizeHtml(html) {
  const tree = new DOMParser().parseFromString(html, "text/html");
  tree.querySelectorAll("script,iframe,object,embed,style").forEach((n) => n.remove());
  tree.querySelectorAll("*").forEach((n) => {
    [...n.attributes].forEach((a) => {
      const name = a.name.toLowerCase();
      if (name.startsWith("on")) n.removeAttribute(a.name);
      if (name === "href" && !/^(https?:|mailto:|#)/i.test(a.value.trim())) n.removeAttribute(a.name);
      if (name === "src" && !/^data:image\//i.test(a.value.trim())) n.removeAttribute(a.name);
    });
    if (n.tagName === "A") { n.setAttribute("target", "_blank"); n.setAttribute("rel", "noopener noreferrer"); }
  });
  return tree.body.innerHTML;
}

// Word files can't open in a browser tab, so .docx is converted to HTML
// in-page (mammoth). Legacy .doc and spreadsheets fall back to download.
// The server's own reason (e.g. "This file is missing from storage") —
// it arrives as a Blob because previews download with responseType blob.
async function downloadError(e) {
  const data = e?.response?.data;
  try {
    const text = data instanceof Blob ? await data.text() : null;
    const detail = text ? JSON.parse(text).detail : data?.detail;
    if (typeof detail === "string") return detail;
  } catch { /* not JSON */ }
  return "This file couldn't be loaded.";
}

/** One preview for every place a document is shown: loads the file once
 * and renders it by kind. Never stuck on "Loading": a failed download
 * shows why. */
export function DocPreviewBody({ doc, height = "h-[70vh]", compact = false }) {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    if (!doc) return undefined;
    let url;
    let cancelled = false;
    setState({ loading: true });
    const kind = previewKind(doc);
    if (!kind) { setState({ none: true }); return undefined; }
    api.get(`/documents/${doc.id}/download`, { responseType: "blob" }).then(async (r) => {
      if (kind === "docx") {
        const mammoth = await import("mammoth/mammoth.browser");
        const { value } = await mammoth.convertToHtml({ arrayBuffer: await r.data.arrayBuffer() });
        if (!cancelled) setState({ kind, html: sanitizeHtml(value) });
        return;
      }
      if (kind === "text") {
        const text = await r.data.text();
        if (!cancelled) setState({ kind, text: text.length > 200000 ? `${text.slice(0, 200000)}\n…` : text });
        return;
      }
      url = window.URL.createObjectURL(new Blob([r.data], { type: doc.content_type }));
      if (!cancelled) setState({ kind, url });
    }).catch(async (e) => { const message = await downloadError(e); if (!cancelled) setState({ error: message }); });
    return () => { cancelled = true; if (url) window.URL.revokeObjectURL(url); };
  }, [doc?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pad = compact ? "p-4" : "p-8";
  if (state.loading) return <div className={`${pad} text-center text-slate-400 text-sm`}>Loading preview…</div>;
  if (state.none) return <div className={`${pad} text-center text-slate-500 text-sm`}>No preview for this file type — download it to open it.</div>;
  if (state.error) return <div className={`${pad} text-center text-rose-700 text-sm`} data-testid="document-preview-error">{state.error}</div>;
  if (state.kind === "image") return <img src={state.url} alt={doc.filename} className="mx-auto max-w-full" />;
  if (state.kind === "pdf") return <iframe src={state.url} title={doc.filename} className={`w-full ${height}`} />;
  if (state.kind === "text") return <pre className={`${compact ? "p-3 text-[11.5px]" : "p-5 text-[12.5px]"} whitespace-pre overflow-auto font-mono text-slate-700`}>{state.text}</pre>;
  return (
    <div className="prose prose-sm max-w-none p-6 text-[13.5px] leading-relaxed [&_table]:border-collapse [&_td]:border [&_td]:border-slate-200 [&_td]:px-2 [&_td]:py-1 [&_p]:mb-2" dangerouslySetInnerHTML={{ __html: state.html }} />
  );
}

export function PreviewDialog({ doc, onClose, onDownload }) {
  return (
    <Dialog open={!!doc} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl" data-testid="document-preview-modal">
        <DialogHeader><DialogTitle className="truncate pr-6">{doc?.filename}</DialogTitle></DialogHeader>
        <div className="mt-2 max-h-[70vh] overflow-auto rounded-lg border border-slate-200 bg-white">
          {doc && <DocPreviewBody doc={doc} />}
        </div>
        <button onClick={() => onDownload(doc)} className="mt-3 self-end flex items-center gap-1.5 px-3 h-9 rounded-lg border border-slate-200 text-[12.5px] font-semibold hover:bg-slate-50">
          <Download size={13} /> Download
        </button>
      </DialogContent>
    </Dialog>
  );
}

const fmtSize = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Groups the flat, all-versions list the API returns into chains — each
// chain is one logical document slot, oldest to newest, tip = active.
function buildChains(docs) {
  const byId = Object.fromEntries(docs.map((d) => [d.id, d]));
  const predecessorOf = {};
  docs.forEach((d) => { if (d.superseded_by) predecessorOf[d.superseded_by] = d.id; });
  const heads = docs.filter((d) => d.status === "active");
  return heads
    .map((head) => {
      const chain = [head];
      let cur = head;
      while (predecessorOf[cur.id]) {
        const prev = byId[predecessorOf[cur.id]];
        chain.unshift(prev);
        cur = prev;
      }
      return chain;
    })
    .sort((a, b) => new Date(b[b.length - 1].created_at) - new Date(a[a.length - 1].created_at));
}

function Thumbnail({ doc }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!doc.content_type?.startsWith("image/")) return;
    let url;
    api.get(`/documents/${doc.id}/download`, { responseType: "blob" }).then((r) => {
      url = window.URL.createObjectURL(r.data);
      setSrc(url);
    }).catch(() => {});
    return () => { if (url) window.URL.revokeObjectURL(url); };
  }, [doc.id, doc.content_type]);

  if (src) return <img src={src} alt={doc.filename} className="w-10 h-10 rounded-md object-cover border border-slate-200 shrink-0" />;
  const Icon = doc.content_type?.startsWith("image/") ? ImageIcon : FileText;
  return (
    <div className="w-10 h-10 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0">
      <Icon size={16} className="text-slate-400" />
    </div>
  );
}

export default function DocumentsPanel({ entityType, entityId, title = "Documents", compact = false, defaultDocType = "other", readOnly = false, onChange }) {
  const [docs, setDocs] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [supersedeTarget, setSupersedeTarget] = useState(null);
  const [docType, setDocType] = useState(defaultDocType);
  const [files, setFiles] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [removeDoc, setRemoveDoc] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const load = () => {
    if (!entityType || !entityId) return;
    api.get("/documents", { params: { entity_type: entityType, entity_id: entityId } }).then((r) => setDocs(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [entityType, entityId]);

  if (!entityId) return null;

  const chains = docs ? buildChains(docs) : [];

  const openUpload = () => { setSupersedeTarget(null); setDocType(defaultDocType); setFiles([]); setShowUpload(true); };
  const openSupersede = (doc) => { setSupersedeTarget(doc); setDocType(doc.doc_type); setFiles([]); setShowUpload(true); };

  // A replacement is one file; a new upload takes as many as were picked.
  const pickFiles = (list) => {
    const picked = Array.from(list || []);
    if (picked.length === 0) return;
    setFiles(supersedeTarget ? picked.slice(0, 1) : (prev) => [...prev, ...picked]);
  };

  const submit = async () => {
    if (files.length === 0) return toast.error("Choose a file");
    setSaving(true);
    try {
      if (supersedeTarget) {
        const form = new FormData();
        form.append("file", files[0]);
        form.append("doc_type", docType);
        await api.post(`/documents/${supersedeTarget.id}/supersede`, form, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Replacement uploaded");
      } else {
        const failed = await uploadDocuments(entityType, entityId, files, docType);
        failed.forEach((f) => toast.error(`${f.name}: ${f.message}`));
        const done = files.length - failed.length;
        if (done > 0) toast.success(done === 1 ? "Document uploaded" : `${done} documents uploaded`);
        if (failed.length > 0 && done === 0) return;
      }
      setShowUpload(false);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      if (previewKind(doc) === "image" || previewKind(doc) === "pdf") {
        window.open(url, "_blank");
      } else {
        // Browsers can't render Office files in a tab — save them instead.
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.filename;
        a.click();
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    } catch (e) {
      toast.error("Failed to download");
    }
  };

  return (
    <div className={compact ? "mt-3" : "mt-8"} data-testid={`documents-panel-${entityType}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={compact ? "text-[12px] font-semibold text-slate-600" : "text-[14.5px] font-bold"}>{title}</div>
        {!readOnly && <button onClick={openUpload} data-testid="upload-document-button" className="flex items-center gap-1.5 px-3 h-11 sm:h-8 rounded-lg border border-slate-200 text-[12.5px] font-semibold hover:bg-slate-50 active:bg-slate-100">
          <Upload size={13} /> Upload
        </button>}
      </div>

      <div className="space-y-2" data-testid="documents-list">
        {chains.length === 0 && <div className="text-slate-400 text-[13px]">No documents uploaded yet.</div>}
        {chains.map((chain) => {
          const current = chain[chain.length - 1];
          const older = chain.slice(0, -1).reverse();
          const isOpen = !!expanded[current.id];
          return (
            <div key={current.id} className="border border-slate-200 rounded-xl p-3" data-testid={`document-row-${current.id}`}>
              <div className="flex items-center gap-3">
                <Thumbnail doc={current} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="ss-pill ss-pill-slate">{docTypeLabel(current.doc_type)}</span>
                    <span className="font-medium text-[13px] truncate">{current.filename}</span>
                  </div>
                  <div className="text-[11.5px] text-slate-500 mt-0.5">
                    {fmtSize(current.size)} · {current.uploaded_by_name} · {fmtDateTime(current.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {previewKind(current) && (
                    <button onClick={() => setPreviewDoc(current)} data-testid={`preview-document-${current.id}`} title="Preview" className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50">
                      <Eye size={14} />
                    </button>
                  )}
                  <button onClick={() => download(current)} data-testid={`download-document-${current.id}`} title="Download" className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50">
                    <Download size={14} />
                  </button>
                  {!readOnly && (
                    <button onClick={() => setRemoveDoc(current)} data-testid={`delete-document-${current.id}`} title="Delete" className="p-2 rounded-lg border border-slate-200 hover:bg-red-50 hover:text-red-600">
                      <Trash2 size={14} />
                    </button>
                  )}
                  {!readOnly && (
                    <button onClick={() => openSupersede(current)} data-testid={`supersede-document-${current.id}`} title="Replace with a new version" className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50">
                      <Repeat size={14} />
                    </button>
                  )}
                </div>
              </div>

              {older.length > 0 && (
                <div className="mt-2 pl-[52px]">
                  <button onClick={() => setExpanded((e) => ({ ...e, [current.id]: !e[current.id] }))} data-testid={`toggle-history-${current.id}`} className="flex items-center gap-1 text-[11.5px] text-slate-500 font-medium">
                    {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    {older.length} earlier version{older.length > 1 ? "s" : ""}
                  </button>
                  {isOpen && (
                    <div className="mt-2 space-y-1.5" data-testid={`version-history-${current.id}`}>
                      {older.map((d) => (
                        <div key={d.id} className="flex items-center gap-2 text-[12px] text-slate-500" data-testid={`superseded-document-${d.id}`}>
                          <span className="ss-pill ss-pill-gray">Superseded</span>
                          <span className="truncate">{d.filename}</span>
                          <span>· {fmtDate(d.created_at)}</span>
                          <button onClick={() => download(d)} className="text-sky-600 font-semibold shrink-0">Download</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent data-testid="upload-document-modal">
          <DialogHeader><DialogTitle>{supersedeTarget ? `Replace "${supersedeTarget.filename}"` : "Upload Document"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-[13px] font-medium">Document Type *</label>
              <SearchableSelect value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="document-type-select" className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 text-sm">
                {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </SearchableSelect>
            </div>
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); pickFiles(e.dataTransfer.files); }}
              data-testid="document-dropzone"
              className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center text-[13px] ${dragging ? "border-sky-400 bg-sky-50" : "border-slate-200"}`}
            >
              {files.length > 0 ? (
                <div className="space-y-1">
                  {files.map((f, i) => (
                    <div key={`${f.name}-${i}`} className="flex items-center justify-center gap-2 text-slate-700 font-medium">
                      <span className="truncate">{f.name}</span> <span className="text-slate-400 font-normal shrink-0">({fmtSize(f.size)})</span>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setFiles(files.filter((_, idx) => idx !== i)); }} className="text-slate-400 hover:text-red-500 shrink-0" title="Remove"><X size={13} /></button>
                    </div>
                  ))}
                  {!supersedeTarget && <div className="text-slate-400 text-[12px] pt-1">Click or drop to add more</div>}
                </div>
              ) : (
                <div className="text-slate-500">Drag files here, or click to choose — PDF, image, or office doc, up to 25MB each</div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                multiple={!supersedeTarget}
                onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }}
                data-testid="document-file-input"
                className="hidden"
              />
            </div>

            {/* Mobile: a dedicated camera button — the charter's receipt/delivery-photo workflows happen on site, so this needs to be obvious, not buried behind the generic dropzone. */}
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              data-testid="camera-capture-button"
              className="sm:hidden w-full h-12 flex items-center justify-center gap-2 rounded-lg border-2 border-sky-200 bg-sky-50 text-sky-700 text-[14px] font-semibold active:bg-sky-100"
            >
              <Camera size={18} /> Take Photo
            </button>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }}
              data-testid="camera-file-input"
              className="hidden"
            />

            <button disabled={saving} onClick={submit} data-testid="confirm-upload-document-button" className="w-full h-11 rounded-lg bg-sky-500 text-white text-[13px] font-semibold disabled:opacity-60">
              {saving ? "Uploading…" : supersedeTarget ? "Upload Replacement" : files.length > 1 ? `Upload ${files.length} Files` : "Upload"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <PreviewDialog doc={previewDoc} onClose={() => setPreviewDoc(null)} onDownload={download} />
      <ConfirmAction open={!!removeDoc} onOpenChange={(o) => !o && setRemoveDoc(null)} title={`Delete "${removeDoc?.filename}"?`}
        description="The file and its earlier versions are removed from this record. Only the uploader or a company admin can do this, and not once the record is approved or synced."
        confirmLabel="Delete" testId="delete-document-dialog"
        onConfirm={async () => { await api.delete(`/documents/${removeDoc.id}`); toast.success("File deleted"); load(); onChange?.(); }} />
    </div>
  );
}
